// Buyer's-premium JSONB column shape (ADR 027 companion / BL-855; strict union BL-982).
//
// Canonical home for the buyer's-premium config shape. It lives in `@auctogon/db`
// — the lowest layer, source of all row/column types (ADR 006) — because the
// generated Drizzle schema stamps `jsonb("buyers_premium").$type<BuyersPremium>()`
// on the `auction_lots` / `auctions` / `auction_rings` / `tenants` columns, and
// `packages/db` cannot import from `@auctogon/core` (core depends on db).
// `@auctogon/core/buyers-premium` re-exports these, so every existing importer of
// `@auctogon/core/buyers-premium` (and the core-admin back-compat re-export) is
// unchanged.
//
// Pure types + one pure function — no `server-only`, no I/O. Safe to re-export
// into client bundles (bidder-web computes buyer's premium client-side via the
// IsomorphicService).
//
// ## BL-982 — why this became a discriminated union
//
// The pre-BL-982 shape was a single all-optional interface, so nothing stopped a
// "Flat" rule from carrying tier data or a "Tiered" rule from carrying a flat
// rate. A 2026-07-24 census of dev found 302 auctions storing buyer's premium in
// **15 distinct key combinations** — the drift behind the BUG-619/558 undercharge
// family. The union below admits exactly two shapes, and `buyersPremiumSchema`
// (`@auctogon/core-admin/schemas/buyers-premium`) is the write-side gate.
//
// ## The read-seam bridge is gone (BL-990, AC 10)
//
// Between BL-982 and BL-990 this module also exported `normalizeStoredBuyersPremium`
// and a `LegacyBuyersPremium` companion type — a read-time bridge that repaired each
// drifted on-disk row so the `.$type<BuyersPremium>()` stamp was true at runtime.
//
// BL-990's reset migration (`db/migrations/20260728162534_bl990_buyers_premium_data_reset.sql`)
// rewrote every row into the strict shape on disk and NULLed every auction- and
// lot-level override, so there is nothing left to repair. The bridge, its
// `LegacyBuyersPremium` input type, and the fee-equivalence guard that pinned it were
// deleted in the same PR. Every BP value now reaching a reader is either canonical or
// NULL, gated on write by `buyersPremiumSchema`
// (`@auctogon/core-admin/schemas/buyers-premium`).
//
// BL-991 finishes the job by dropping `tenants.default_buyers_premium` and
// `auction_lots.buyers_premium` outright.

/** How a buyer's-premium schedule is computed. */
export type BpCalculationType = 'Flat' | 'Tiered';

/**
 * How a tiered buyer's-premium schedule's tiers combine.
 * - `'Marginal'` — each tier's rate applies only to its slice of the hammer
 *   (tax-bracket style). This is the historical behaviour and the default.
 * - `'Bracket'` — the whole hammer is charged at the single flat rate for the
 *   tier it lands in (not a blend).
 */
export type BuyersPremiumCombineMode = 'Marginal' | 'Bracket';

/** A single tier of a tiered buyer's-premium schedule. */
export interface BuyersPremiumTier {
  /** Percentage rate for this tier (e.g. 18 for 18%). */
  rate: number;
  /**
   * Upper bound (inclusive) for this tier. `null` is the canonical on-disk form
   * for the unbounded remainder tier (JSONB cannot store `Infinity`), and only
   * the LAST tier may carry it — enforced by `buyersPremiumSchema`.
   */
  threshold: number | null;
}

/** Schedule-level floor/ceiling applied after the flat/tier calculation. */
interface BuyersPremiumBounds {
  /** When calculated BP > 0, raise it to at least this. `null`/absent → no floor. */
  minFee?: number | null;
  /** Total BP never exceeds this. `null`/absent → no ceiling. */
  maxFee?: number | null;
}

/**
 * Buyer's-premium configuration — the canonical, strict shape.
 *
 * Exactly two variants, discriminated on `type`. A `Flat` schedule has a rate and
 * no tiers; a `Tiered` schedule has tiers and an explicit combine mode. The
 * retired `flatCap` field is absent — BL-990's reset migration folded it into
 * `maxFee` on the Flat path and dropped it on the Tiered path, permanently.
 */
export type BuyersPremium =
  | ({ type: 'Flat'; flatRate: number } & BuyersPremiumBounds)
  | ({
      type: 'Tiered';
      tiers: BuyersPremiumTier[];
      combineMode: BuyersPremiumCombineMode;
    } & BuyersPremiumBounds);

/**
 * @deprecated BL-982 — use {@link BuyersPremium}. Retained as an alias so the
 * ~117 existing references across 28 files keep compiling without a rename
 * sweep; a follow-up item drops it.
 */
export type BuyersPremiumInput = BuyersPremium;

/**
 * @deprecated BL-982 — use {@link BuyersPremiumTier}.
 */
export type BuyersPremiumTierInput = BuyersPremiumTier;

/**
 * Anything the calculator or the formatters accept as a buyer's-premium input.
 *
 * Post-BL-990 every stored value is either canonical or absent, so this is just
 * "a schedule, or nothing" — the nullable/optional wrapper exists because every
 * cascade rung is legitimately unset.
 */
export type BuyersPremiumLike = BuyersPremium | null | undefined;

/**
 * A positive, finite bound — anything else (0, null, NaN, Infinity) means "no bound".
 *
 * Survives BL-990's deletion of the read-seam normalizer because it is not legacy
 * residue: it is the live predicate deciding whether a `minFee` / `maxFee` is
 * *active*. The calculator applies a bound only when it passes this test, and the
 * summary formatter renders a "min"/"cap" segment only when it passes this test.
 * Two copies would drift the moment the bound rule changes (BL-983).
 *
 * Takes `unknown` so it can gate a value straight off a JSONB read.
 */
export function activeBound(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Whether a value is structurally complete enough to be a real buyer's-premium
 * schedule — i.e. something that can be billed, rendered, or stop the cascade.
 *
 * ONE definition, three consumers: the calculator (`@auctogon/core/buyers-premium`
 * `calculateBuyersPremium`), the summary formatters (`format.ts`), and the cascade
 * resolver's `isUsableRung` (`resolve.ts`), which delegates here. That sharing is
 * the point — before BL-990 the calculator got its guard from
 * `normalizeStoredBuyersPremium` while the resolver used its own predicate, and the
 * two DISAGREED on one shape: a `{type:'Flat'}` with no rate normalized to
 * `flatRate: 0` (a cascade-stopping 0% rule) but was rejected by `isUsableRung` (a
 * fall-through to the house default). Deleting the normalizer removed one half of
 * that disagreement; defining the predicate once removes the other.
 *
 * Structural completeness only — NOT "does this produce a non-zero premium". An
 * explicit `flatRate: 0` is a deliberate operator waiver and IS usable; it must be
 * honoured rather than falling through to a nonzero parent (BUG-506).
 *
 * Typed as a guard so callers narrow to {@link BuyersPremium} without a cast. It
 * takes {@link BuyersPremiumLike} because the runtime inputs are JSONB reads and
 * admin editor drafts, which can be malformed even where the compiler says
 * otherwise — a tier-less `Tiered` reaching the calculator threw a TypeError
 * (a 500 on the invoice path) rather than billing 0.
 *
 * @param bp - A value read from any BP column, an editor draft, or nothing.
 * @returns `true` when the value is a complete Flat or Tiered schedule.
 * @sideEffect None — pure.
 */
export function isUsableBuyersPremium(bp: BuyersPremiumLike): bp is BuyersPremium {
  if (!bp) return false;
  if (bp.type === 'Flat') return typeof bp.flatRate === 'number';
  if (bp.type === 'Tiered') {
    return (
      Array.isArray(bp.tiers) &&
      bp.tiers.length > 0 &&
      bp.tiers.every((t) => typeof t?.rate === 'number')
    );
  }
  return false;
}
